import type { Listing } from "./listing-types";

export const JOB_CURRENCIES = ["GBP", "EUR", "USD", "CAD", "AUD", "NZD", "CHF", "JPY", "NOK", "SEK", "DKK", "ISK", "PLN", "CZK", "HUF", "RON", "BGN", "TRY", "ZAR", "AED", "THB", "IDR", "INR", "MXN", "BRL", "CLP", "ARS", "CNY", "HKD", "SGD", "KRW", "MYR", "PHP", "VND"] as const;
export const JOB_PAY_PERIODS = { hour: "Hour", day: "Day", week: "Week", month: "Month", year: "Year", season: "Season" } as const;
export const JOB_PAY_BASES = { unspecified: "Unspecified", gross: "Gross (before tax)", net: "Net (as declared by employer)" } as const;
export const JOB_ACCOMMODATION = { unspecified: "Unspecified", provided: "Provided", assisted: "Help finding accommodation", not_provided: "Not provided" } as const;
export const JOB_MEALS = { unspecified: "Unspecified", included: "Included", discounted: "Discounted", not_provided: "Not provided" } as const;
export const JOB_TRANSPORT = { unspecified: "Unspecified", included: "Included", assisted: "Travel assistance", not_provided: "Not provided" } as const;
export const JOB_COST_PAYMENT = { unspecified: "Unspecified", pay_deduction: "Deducted from pay", separate: "Paid separately" } as const;

/** Missing or null values are unspecified. Legacy text and perks are never inferred. */
export type JobTerms = {
  pay_min?: number | null;
  pay_max?: number | null;
  pay_currency?: typeof JOB_CURRENCIES[number] | null;
  pay_period?: keyof typeof JOB_PAY_PERIODS | null;
  pay_basis?: keyof typeof JOB_PAY_BASES | null;
  pay_notes?: string | null;
  hours_min?: number | null;
  hours_max?: number | null;
  hours_notes?: string | null;
  accommodation?: keyof typeof JOB_ACCOMMODATION | null;
  accommodation_cost?: number | null;
  accommodation_currency?: typeof JOB_CURRENCIES[number] | null;
  accommodation_period?: keyof typeof JOB_PAY_PERIODS | null;
  accommodation_payment?: keyof typeof JOB_COST_PAYMENT | null;
  accommodation_notes?: string | null;
  meals?: keyof typeof JOB_MEALS | null;
  meals_notes?: string | null;
  transport?: keyof typeof JOB_TRANSPORT | null;
  transport_notes?: string | null;
};

const numericFields = { pay_min: 1_000_000, pay_max: 1_000_000, hours_min: 168, hours_max: 168, accommodation_cost: 1_000_000 } as const;
const textFields = ["pay_notes", "hours_notes", "accommodation_notes", "meals_notes", "transport_notes"] as const;
const enumFields = {
  pay_currency: JOB_CURRENCIES, accommodation_currency: JOB_CURRENCIES,
  pay_period: Object.keys(JOB_PAY_PERIODS), accommodation_period: Object.keys(JOB_PAY_PERIODS),
  pay_basis: Object.keys(JOB_PAY_BASES), accommodation: Object.keys(JOB_ACCOMMODATION),
  accommodation_payment: Object.keys(JOB_COST_PAYMENT), meals: Object.keys(JOB_MEALS), transport: Object.keys(JOB_TRANSPORT),
};

export function validateJobTerms(value: unknown): string | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return "Job terms must be an object.";
  const terms = value as Record<string, unknown>;
  const allowed = new Set([...Object.keys(numericFields), ...textFields, ...Object.keys(enumFields)]);
  if (Object.keys(terms).some((key) => !allowed.has(key))) return "Unrecognised job term.";
  for (const [key, max] of Object.entries(numericFields)) {
    const amount = terms[key];
    if (amount == null) continue;
    if (typeof amount !== "number" || !Number.isFinite(amount) || amount < 0 || amount > max || Math.abs(amount * 100 - Math.round(amount * 100)) > 0.000001) {
      return key.startsWith("hours") ? "Weekly hours must be between 0 and 168, with at most two decimal places." : "Amounts must be between 0 and 1,000,000, with at most two decimal places.";
    }
  }
  for (const key of textFields) {
    if (terms[key] != null && (typeof terms[key] !== "string" || (terms[key] as string).length > 600)) return "Each terms note must be 600 characters or fewer.";
  }
  for (const [key, options] of Object.entries(enumFields)) {
    if (terms[key] != null && (typeof terms[key] !== "string" || !(options as readonly string[]).includes(terms[key] as string))) return "Choose a valid terms option or leave it unspecified.";
  }
  const t = terms as JobTerms;
  if (t.pay_min != null && t.pay_max != null && t.pay_min > t.pay_max) return "Maximum pay must be at least minimum pay.";
  if (t.hours_min != null && t.hours_max != null && t.hours_min > t.hours_max) return "Maximum hours must be at least minimum hours.";
  if ((t.pay_min != null || t.pay_max != null) && (!t.pay_currency || !t.pay_period)) return "Choose a currency and pay period for the pay amount.";
  if (t.accommodation_cost != null) {
    if (!t.accommodation || !["provided", "assisted"].includes(t.accommodation)) return "Accommodation costs need provided accommodation or help finding it.";
    if (!t.accommodation_currency || !t.accommodation_period) return "Choose a currency and period for accommodation costs.";
  }
  return null;
}

function range(min?: number | null, max?: number | null): string {
  const number = (value: number) => value.toLocaleString("en-GB", { maximumFractionDigits: 2 });
  if (min != null && max != null) return min === max ? number(min) : `${number(min)} to ${number(max)}`;
  if (min != null) return `From ${number(min)}`;
  if (max != null) return `Up to ${number(max)}`;
  return "Unspecified";
}

export function formatJobPay(terms?: JobTerms | null): string {
  if (terms?.pay_min == null && terms?.pay_max == null) return "Pay unspecified";
  return `${range(terms?.pay_min, terms?.pay_max)} ${terms?.pay_currency ?? "(currency unspecified)"} per ${terms?.pay_period ?? "unspecified period"} · ${terms?.pay_basis === "gross" ? "gross" : terms?.pay_basis === "net" ? "net, employer declared" : "gross/net unspecified"}`;
}

export function formatJobDate(date: string): string {
  return new Date(date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}

export function formatJobDates(listing: Pick<Listing, "start_date" | "end_date" | "dates_flexible">): string {
  const dates = listing.start_date && listing.end_date
    ? `${formatJobDate(listing.start_date)} to ${formatJobDate(listing.end_date)}`
    : listing.start_date ? `From ${formatJobDate(listing.start_date)}; end unspecified`
      : listing.end_date ? `Start unspecified; until ${formatJobDate(listing.end_date)}` : "Dates unspecified";
  return `${dates} · ${listing.dates_flexible ? "Flexible" : "Flexibility not declared"}`;
}

export function jobHasEnded(listing: Pick<Listing, "end_date">, today = new Date().toISOString().slice(0, 10)): boolean {
  return Boolean(listing.end_date && listing.end_date < today);
}

export function vacancyFreshness(listing: Pick<Listing, "status" | "end_date" | "vacancy_confirmed_at">, today = new Date().toISOString().slice(0, 10)): string {
  if (listing.status === "fulfilled") return "Role filled";
  if (listing.status === "archived") return "Role archived";
  if (jobHasEnded(listing, today)) return "Listed end date has passed";
  if (!listing.vacancy_confirmed_at || !Number.isFinite(Date.parse(listing.vacancy_confirmed_at)) || listing.vacancy_confirmed_at.slice(0, 10) > today) return "Vacancy not yet reconfirmed";
  return `Employer confirmed hiring ${formatJobDate(listing.vacancy_confirmed_at)}`;
}

export function jobTermsRows(listing: Pick<Listing, "job_terms" | "pay_info" | "start_date" | "end_date" | "dates_flexible" | "status" | "vacancy_confirmed_at">): { label: string; value: string; note?: string | null }[] {
  const t = listing.job_terms ?? {};
  return [
    { label: "Vacancy", value: vacancyFreshness(listing) },
    { label: "Dates", value: formatJobDates(listing) },
    { label: "Pay", value: formatJobPay(t), note: t.pay_notes },
    { label: "Expected weekly hours", value: t.hours_min == null && t.hours_max == null ? "Unspecified" : `${range(t.hours_min, t.hours_max)} hours`, note: t.hours_notes },
    { label: "Accommodation", value: JOB_ACCOMMODATION[t.accommodation ?? "unspecified"], note: t.accommodation_notes },
    { label: "Accommodation cost", value: t.accommodation_cost == null ? "Unspecified" : `${t.accommodation_cost.toLocaleString("en-GB")} ${t.accommodation_currency} per ${t.accommodation_period} · ${JOB_COST_PAYMENT[t.accommodation_payment ?? "unspecified"]}${!t.accommodation_payment || t.accommodation_payment === "unspecified" ? " payment method" : ""}` },
    { label: "Meals", value: JOB_MEALS[t.meals ?? "unspecified"], note: t.meals_notes },
    { label: "Transport", value: JOB_TRANSPORT[t.transport ?? "unspecified"], note: t.transport_notes },
    { label: "Employer's pay wording", value: listing.pay_info || "Unspecified" },
  ];
}
